/********************************************************************************
 * Copyright (c) 2021 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const {
	getColumnPropertiesAt,
	getCombinedCellPropertiesAt,
	getRowPropertiesAt
} = require('../../model/utils');
const CellRange = require('../../model/CellRange');
const { getRangeIndex, iterateCellRange } = require('../utils');
const { CompoundRequestCommand, RequestCommand } = require('./RequestCommands');
const SetPropertiesCommand = require('./SetPropertiesCommand');
const SetCellsCommand = require('./SetCellsCommand');

const doPasteProperties = (action) => action === 'all' || action === 'formats';
const isEmptyProperties = (props) => props == null || !Object.keys(props).length;

const clear = (properties = {}) => {
	Object.keys(properties).forEach((key) => {
		properties[key] = null;
	});
};
const clearProperties = (props = {}) => {
	const { attributes, formats, textFormats } = props;
	clear(attributes);
	clear(formats);
	clear(textFormats);
};
const collectCutDescriptor = (all, sheet) => (cell, ref) => {
	// have set sheet properties to null too!
	const props = getCombinedCellPropertiesAt(ref, sheet, sheet.cells);
	clearProperties(props);
	return all.push({ ref, reference: `${ref.col}${ref.row}`, properties: props });
};
const getCutDescriptors = (range) => {
	// run through range and collect each ref and mark props as to be deleted...
	const descriptors = [];
	iterateCellRange(range, collectCutDescriptor(descriptors, range.sheet));
	return descriptors;
};

const getValue = (action) => {
	const doIt = action === 'all' || action === 'values';
	return (cell) => (doIt && cell ? cell.getValue() : undefined);
};
const getFormula = (action) => {
	const doIt = action === 'all' || action === 'formulas';
	return (cell) => {
		const expr = doIt && cell ? cell.getExpression() : undefined;
		return expr ? expr.getFormula() : undefined;
	};
};
const getProperties = (action, sheet) => {
	const doIt = doPasteProperties(action);
	return (ref) => (doIt ? getCombinedCellPropertiesAt(ref, sheet, sheet.cells) : undefined);
};
const collectCellDescriptors = (action, sheet, allCells) => {
	const value = getValue(action);
	const formula = getFormula(action);
	const properties = getProperties(action, sheet, sheet);
	return (cell, ref) =>
		allCells.push({ ref, value: value(cell), formula: formula(cell), properties: properties(ref) });
};
const getCellDescriptors = (range, action) => {
	const descriptors = [];
	iterateCellRange(range, collectCellDescriptors(action, range.sheet, descriptors));
	return descriptors;
};

const NR_REG_EX = /^\D+/g;
const reference2ref = (str) => {
	const nr = str.replace(NR_REG_EX, '');
	return {
		col: str.substring(0, str.length - nr.length),
		row: Number(nr)
	};
};
class PasteCellsCommand extends RequestCommand {
	constructor(srcrange, trgtrange, action, cut) {
		super(trgtrange.sheet);

		const sourceStreamsheetId = srcrange.sheet.id;

		this.info = {
			descriptors: getCellDescriptors(srcrange, action),
			range: trgtrange
				.copy()
				.shiftToSheet()
				.toString(),
			sourceStreamsheetId,
			cut
		};
		this.undoInfo = {
			descriptors: undefined,
			range: this.info.range,
			cut
		};
	}

	addSeries(cmd) {
		if (cmd) {
			const { cells = [] } = cmd;
			this.info.series = cells.map((cell) => ({ ...cell, ref: reference2ref(cell.reference) }));
		}
	}

	getExecuteRequest() {
		return this.createRequest(this.info);
	}
	getRedoRequest() {
		return this.getExecuteRequest();
	}
	getUndoRequest() {
		return this.createRequest(this.undoInfo, 'command.server.ReplaceCellsCommand');
	}

	handleResult(result) {
		const { descriptors } = result;
		if (!this.undoInfo.descriptors) this.undoInfo.descriptors = descriptors;
	}
}

const getColSectionIndex = (range, target) => {
	const startX = range.getX1();
	const targetStartX = target.getX1();
	const tmprange = new CellRange(target.sheet, 0, 0, 0, 0);
	const { maxColumns, maxRows } = target.sheet;
	return (index) => {
		const colIndex = Math.min(targetStartX + (index - startX), maxColumns);
		return getRangeIndex(tmprange.set(colIndex, 1, colIndex, maxRows));
	};
};
const getRowSectionIndex = (range, target) => {
	const startY = range.getY1();
	const targetStartY = target.getY1();
	const tmprange = new CellRange(target.sheet, 0, 0, 0, 0);
	const { maxColumns, maxRows } = target.sheet;
	return (index) => {
		const rowIndex = Math.min(targetStartY + (index - startY), maxRows);
		return getRangeIndex(tmprange.set(1, rowIndex, maxColumns, rowIndex));
	};
};
const collectColumnProperties = (range, target) => {
	const allProps = [];
	const getSectionIndex = getColSectionIndex(range, target);
	range.enumerateColumns((index) => {
		allProps.push({
			ref: { col: getSectionIndex(index) },
			properties: getColumnPropertiesAt(index, range.sheet.columns)
		});
	});
	return allProps;
};
const collectRowProperties = (range, target) => {
	const allProps = [];
	const getSectionIndex = getRowSectionIndex(range, target);
	range.enumerateRows((index) => {
		allProps.push({
			ref: { row: getSectionIndex(index) },
			properties: getRowPropertiesAt(index, range.sheet.rows)
		});
	});
	return allProps;
};
const getSectionProperties = (range, target) => {
	if (range.isColumnRange() && target.isColumnRange()) return { cols: collectColumnProperties(range, target) };
	return range.isRowRange() && target.isRowRange() ? { rows: collectRowProperties(range, target) } : {};
};

class PastePropertiesCommand extends SetPropertiesCommand {
	handleResult(result) {
		const { oldProperties = {} } = result;
		if (!this._oldProperties) this._oldProperties = oldProperties;
	}
}

class PasteCellsCompoundCommand extends CompoundRequestCommand {
	constructor(target, { cut, range, data }, action /* , fill */) {
		super();

		// only required to conform to PateCellsFromClipboardCommand
		this.target = target.copy();

		// data handling...
		const source = range.copy();
		data.evaluate();
		source.data = data;

		// excel seems to copy & paste all on cut...
		if (cut) {
			action = 'all';
			this.add(new SetCellsCommand(source.sheet, getCutDescriptors(source)));
		}
		if (doPasteProperties(action)) {
			// get section properties only (cells props are pasted by internal PasteCellsCommand)
			const properties = getSectionProperties(range, target);
			if (!isEmptyProperties(properties.cols) || !isEmptyProperties(properties.rows)) {
				this.add(new PastePropertiesCommand(target.sheet, properties));
			}
		}
		// finally
		this.pasteCellsCmd = new PasteCellsCommand(source, target, action, cut);
		this.add(this.pasteCellsCmd);
		this.saveSelection([target]);
	}

	// conform to legacy PateCellsFromClipboardCommand
	addCut(/* cmd */) {}
	addSeries(cmd) {
		this.pasteCellsCmd.addSeries(cmd);
	}
	getCompleteTargetRange() {
		return this.target;
	}
	// ~ PasteCellsFromClipboardCommand
}

module.exports = PasteCellsCompoundCommand;
